import { apiScales } from "@/api/scale.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiFormViewScalePayload } from "@/types/forms/form-scale.types.ts";
import { ApiFormViewScaleFilter } from "@/types/forms/form-view.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Control, FieldValues } from "react-hook-form";

const scaleKeys = createQueryKeys("scales", {
  scales: null,
  search: (filter: ApiFormViewScaleFilter) => ({ queryKey: [filter] }),
});

export const useFormScalesSearch = (filter: ApiFormViewScaleFilter, enabled: boolean = true) => {
  return useQuery({
    ...scaleKeys.search(filter),
    enabled: enabled,
    queryFn: async ({ signal }) => {
      const result = await apiScales.search({ ...filter }, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
  });
};

export const useFormScalesSearchInline = (enabled = true) => {
  const [filter, setFilter] = useState<ApiFormViewScaleFilter>({
    page_number: 1,
    text: undefined,
  });
  const { data, isLoading } = useFormScalesSearch(filter, enabled);
  const options = useMemo(() => {
    return data?.items || ([] as SelectItem[]);
  }, [data]);

  function search(text: string | undefined) {
    setFilter({ ...filter, text: text });
  }

  return {
    data,
    isLoading,
    options,
    search,
  };
};

export interface ScalesSaveParams {
  id?: number;
  payload: ApiFormViewScalePayload;
}

export const useFormScalesSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: ScalesSaveParams) => {
      const result = id ? await apiScales.update(id, payload) : await apiScales.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scaleKeys.search._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useFormScalesDelete = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiScales.delete(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scaleKeys.search._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};
